/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import moment from 'moment';

import useRouter from 'js/lib/useRouter';
import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Typography2 as Typography, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import { CML, CMLUtils } from 'bundles/cml';
import TogglableContent from 'bundles/page/components/TogglableContent';
import RatingLine from 'bundles/reviews-common/components/cds/RatingLine';
import type { ProductReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import type { ReviewWithAuthor } from 'bundles/reviews/components/ReviewsTable';
import ToggleButtonForTogglableContent from 'bundles/reviews/components/ToggleButtonForTogglableContent';

import _t from 'i18n!nls/reviews';

const REVIEW_TEXT_COLLAPSED_HEIGHT = 84;

export function getReviewAuthorName(review: ReviewWithAuthor): string {
  return (review as ProductReview).fullName ?? review.users?.publicDemographics?.fullName;
}

const styles = {
  card: css`
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr) auto;
    align-items: flex-start;
    gap: var(--cds-spacing-200);
    width: 100%;
    padding: var(--cds-spacing-200);
    box-sizing: border-box;
    border: 1px solid var(--cds-color-grey-300);
    border-radius: var(--cds-border-radius-100);
    background: var(--cds-color-neutral-background-primary);
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      grid-template-columns: 1fr;
    }
  `,
  authorColumn: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,
  authorInfo: css`
    min-width: 0;
  `,
  avatar: css`
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--cds-color-callouts-tertiary);
    border: 1px solid var(--cds-color-grey-300);
    color: var(--cds-color-neutral-background-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  `,
  authorName: css`
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
  `,
  main: css`
    flex: 1;
    min-width: 0;
  `,
  meta: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-100);
  `,
  rating: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  reviewText: css`
    .content-inner p:first-of-type {
      margin-top: 0;
    }

    .review-card-cml,
    .review-card-cml p {
      font-size: 14px;
      line-height: 20px;
    }
  `,
  helpful: css`
    justify-self: end;

    ${breakpoints.down('sm')} {
      width: 100%;
      justify-self: start;
    }
  `,
  helpfulButtonText: css`
    color: var(--cds-color-interactive-secondary-inactive);
    display: inline-flex;
    align-items: center;
  `,
  dotSeparator: css`
    margin: 0 var(--cds-spacing-50);
    color: var(--cds-color-neutral-primary-weak);
  `,
};

type ReviewTableCardProps = {
  review: ReviewWithAuthor;
  userId?: number;
  onMarkHelpful?: (reviewId: string, voteType: string) => void;
};

export function ReviewTableCard({ review, userId, onMarkHelpful }: ReviewTableCardProps) {
  const router = useRouter();
  const [helpfulCount, setHelpfulCount] = React.useState(review.mostHelpfulVoteCount ?? 0);
  const [isMarkedHelpful, setIsMarkedHelpful] = React.useState(review.isMarkedHelpful);
  const isMobileViewport = useMediaQuery(breakpoints.down('sm'));

  const authorName = getReviewAuthorName(review);

  const helpfulCountLabel = helpfulCount > 0 ? `(${helpfulCount})` : '';
  const voteType = isMarkedHelpful ? 'UNMARKED_VOTE' : 'HELPFUL_VOTE';
  const helpfulButtonLabel = _t('This was helpful #{count}', { count: helpfulCountLabel });
  const helpfulButtonText = isMobileViewport
    ? _t('Helpful #{count}', { count: helpfulCountLabel })
    : helpfulButtonLabel;

  const handleHelpfulClick = () => {
    if (!userId) {
      router.replace({
        pathname: router.location.pathname,
        query: { ...router.location.query, authMode: 'signup' },
      });
      return;
    }

    if (onMarkHelpful) {
      onMarkHelpful(review.id, voteType);
      if (voteType === 'HELPFUL_VOTE') {
        setHelpfulCount((c) => c + 1);
        setIsMarkedHelpful(true);
      } else {
        setHelpfulCount((c) => Math.max(0, c - 1));
        setIsMarkedHelpful(false);
      }
    }
  };

  return (
    <article
      css={styles.card}
      data-testid={`review-table-card-${review.id}`}
      aria-label={_t('Review by #{author}', { author: authorName })}
    >
      <div css={styles.authorColumn}>
        <div css={styles.avatar} aria-hidden="true">
          {authorName.charAt(0).toUpperCase()}
        </div>
        <span css={[styles.authorInfo, styles.authorName]}>
          <Typography variant="subtitleSmall" component="span">
            {authorName}
          </Typography>
        </span>
      </div>
      <div css={styles.main}>
        <div css={styles.meta}>
          <div css={styles.rating}>
            <RatingLine rating={review.rating} />
            <Typography css={styles.dotSeparator} component="span" aria-hidden>
              ·
            </Typography>
            <Typography variant="bodySecondary" color="supportText" component="span">
              {_t('Reviewed #{date}', {
                date: formatDateTimeDisplay(moment(review.reviewedAt).locale(_t.getLocale()), MED_DATE_ONLY_DISPLAY),
              })}
            </Typography>
          </div>
        </div>
        {review.reviewText?.cml && (
          <div css={styles.reviewText}>
            <TogglableContent
              childrenWrapperTag="div"
              collapsedHeight={REVIEW_TEXT_COLLAPSED_HEIGHT}
              disableCollapseScroll
              renderToggleButtonContentFunction={ToggleButtonForTogglableContent}
              toggleButtonClassName="bold"
              toggleButtonType="button"
              trackingName="review-table-card-text"
              useSmartCollapse
            >
              <CML
                className="review-card-cml"
                cml={CMLUtils.create(review.reviewText.cml.value, review.reviewText.cml.dtdId)}
              />
            </TogglableContent>
          </div>
        )}
      </div>
      <div css={styles.helpful}>
        <Button
          variant="ghostSecondary"
          icon={
            isMarkedHelpful ? (
              <ThumbsUpFilledIcon color="support" size="small" />
            ) : (
              <ThumbsUpIcon color="support" size="small" />
            )
          }
          iconPosition="after"
          onClick={handleHelpfulClick}
          disabled={Boolean(userId && !onMarkHelpful)}
          aria-label={helpfulButtonLabel}
          edgeAlign={isMobileViewport ? 'start' : undefined}
        >
          <Typography variant="actionSecondary" component="span" css={styles.helpfulButtonText}>
            {helpfulButtonText}
          </Typography>
        </Button>
      </div>
    </article>
  );
}

export default ReviewTableCard;
